import React, { useEffect, useState } from "react";
import { api, fmtGNF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, TrendingUp, AlertCircle, GraduationCap, Wallet, Percent, RefreshCw, FileText, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function KPICard({ icon: Icon, label, value, color = "#1E3A8A", suffix = "" }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase text-slate-500">{label}</div>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="text-3xl font-extrabold tabular-nums" style={{ color }}>{value}{suffix}</div>
    </div>
  );
}

export default function ScolariteDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scolarite/dashboard");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="scolarite-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><GraduationCap className="h-3 w-3" /><span>RESPONSABLE SCOLARITÉ</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Tableau de bord — Scolarité</h1>
          <p className="text-slate-500 mt-1">Vue consolidée : inscriptions, plans, remises, recouvrement.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 mr-2 ${loading && "animate-spin"}`} />Actualiser</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Effectif total" value={data?.total_students || 0} />
        <KPICard icon={FileText} label="Plans actifs" value={data?.active_plans || 0} color="#166534" />
        <KPICard icon={Percent} label="Recouvrement" value={data?.recouvrement_percentage || 0} suffix="%" color="#EA580C" />
        <KPICard icon={AlertCircle} label="Remises en attente" value={data?.pending_discounts || 0} color="#DC2626" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase text-slate-500 mb-1">Total encaissé</div>
          <div className="text-2xl font-extrabold text-[#166534] tabular-nums">{fmtGNF(data?.total_paid_gnf || 0)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase text-slate-500 mb-1">Total attendu</div>
          <div className="text-2xl font-extrabold text-[#1E3A8A] tabular-nums">{fmtGNF(data?.total_expected_gnf || 0)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase text-slate-500 mb-1">Impayés</div>
          <div className="text-2xl font-extrabold text-[#DC2626] tabular-nums">{fmtGNF(data?.impayes_gnf || 0)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50 font-heading font-bold text-[#1E3A8A] flex items-center justify-between">
            <span><AlertCircle className="h-4 w-4 inline mr-2" />Top impayés</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Élève</th>
                  <th className="px-3 py-2 text-left">Classe</th>
                  <th className="px-3 py-2 text-right">Impayé</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_overdue || []).map(r => (
                  <tr key={r.student_id} className="border-t border-slate-100" data-testid={`overdue-${r.student_id}`}>
                    <td className="px-3 py-2 font-medium">{r.student_name}</td>
                    <td className="px-3 py-2 text-slate-600">{r.class_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#DC2626]">{fmtGNF(r.overdue_amount)}</td>
                  </tr>
                ))}
                {(!data?.top_overdue || data.top_overdue.length === 0) && (
                  <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400">Aucun impayé 🎉</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="font-heading font-bold text-[#1E3A8A] mb-4">Actions rapides</div>
          <div className="space-y-2">
            <Link to="/dashboard/scolarite/inscriptions" className="block bg-slate-50 hover:bg-blue-50 rounded-lg p-3 transition" data-testid="quick-enroll">
              <div className="font-semibold text-[#1E3A8A]">📥 Nouvelle inscription</div>
              <div className="text-xs text-slate-500">Créer un dossier élève + génération matricule</div>
            </Link>
            <Link to="/dashboard/scolarite/plans" className="block bg-slate-50 hover:bg-blue-50 rounded-lg p-3 transition" data-testid="quick-plan">
              <div className="font-semibold text-[#1E3A8A]">💳 Créer un plan de paiement</div>
              <div className="text-xs text-slate-500">Mensuel, annuel, trimestriel ou personnalisé</div>
            </Link>
            <Link to="/dashboard/scolarite/remises" className="block bg-slate-50 hover:bg-blue-50 rounded-lg p-3 transition" data-testid="quick-discount">
              <div className="font-semibold text-[#1E3A8A]">🎁 Demander une remise</div>
              <div className="text-xs text-slate-500">Envoyer au Fondateur pour validation</div>
            </Link>
            <Link to="/dashboard/scolarite/encaisser" className="block bg-slate-50 hover:bg-blue-50 rounded-lg p-3 transition" data-testid="quick-cashier">
              <div className="font-semibold text-[#1E3A8A]">💵 Encaisser un paiement</div>
              <div className="text-xs text-slate-500">Espèces / Orange Money / Mobile Money / Virement</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
